import { useState } from "react";
import { Copy, Eye, EyeOff, KeyRound, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useAgentAccessPassword,
  useRemoveAccessPassword,
  useSetAccessPassword,
} from "@/hooks/agent/useAgentAccess";
import { Agent } from "@/types/agent/agent";
import {
  ACCESS_PASSWORD_GRANT_DAYS,
  ACCESS_PASSWORD_MIN_LENGTH,
  AccessPasswordGrantDays,
} from "@/types/agent/agent-access";
import ShareSettingsSection from "./ShareSettingsSection";

interface PasswordAccessSectionProps {
  genie: Agent;
  shareUrl: string;
}

const DEFAULT_BRAND_COLOR = "#4E9CFF";
const GENERATED_ALPHABET = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";

const DURATION_LABELS: Record<AccessPasswordGrantDays, string> = {
  90: "90 days",
  180: "6 months",
  365: "1 year",
};

const generatePassword = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  return Array.from(bytes, (byte) => GENERATED_ALPHABET[byte % GENERATED_ALPHABET.length]).join("");
};

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export default function PasswordAccessSection({ genie, shareUrl }: PasswordAccessSectionProps) {
  const { data: status, isLoading } = useAgentAccessPassword(genie.id);
  const setMutation = useSetAccessPassword(genie.id);
  const removeMutation = useRemoveAccessPassword(genie.id);
  const [showForm, setShowForm] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [grantDays, setGrantDays] = useState<AccessPasswordGrantDays>(ACCESS_PASSWORD_GRANT_DAYS[0]);
  const [savedPassword, setSavedPassword] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const brandColor = genie.branding?.primaryColor || genie.brand_color || DEFAULT_BRAND_COLOR;
  const isEnabled = !!status?.enabled;
  const isPasswordValid = password.trim().length >= ACCESS_PASSWORD_MIN_LENGTH;

  const openForm = () => {
    setPassword("");
    setShowPassword(false);
    setGrantDays(status?.grant_days ?? ACCESS_PASSWORD_GRANT_DAYS[0]);
    setSavedPassword(null);
    setShowForm(true);
  };

  const handleGenerate = () => {
    setPassword(generatePassword());
    setShowPassword(true);
  };

  const handleSave = () => {
    if (!isPasswordValid) return;
    setMutation.mutate(
      { password, grant_days: grantDays },
      {
        onSuccess: () => {
          setSavedPassword(password);
          setShowForm(false);
          setPassword("");
        },
      },
    );
  };

  const handleDurationChange = (value: string) => {
    setMutation.mutate({ grant_days: Number(value) as AccessPasswordGrantDays });
  };

  const handleRemove = () => {
    removeMutation.mutate(undefined, {
      onSuccess: () => {
        setConfirmRemove(false);
        setSavedPassword(null);
      },
    });
  };

  const copyText = async (text: string, message: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(message);
    } catch {
      toast.error("Couldn't copy. Select the text to copy it manually.");
    }
  };

  const invitation = savedPassword
    ? `You're invited to talk to ${genie.name}.\n${shareUrl}\nPassword: ${savedPassword}`
    : "";

  const summary = isLoading
    ? undefined
    : status?.enabled
      ? `On · each unlock lasts ${DURATION_LABELS[status.grant_days ?? ACCESS_PASSWORD_GRANT_DAYS[0]]}`
      : "Off";

  return (
    <ShareSettingsSection title="Password Protection" summary={summary}>
      <div>
        <p className="text-sm text-gray-600 mb-4">
          Anyone with the genie link and this password can open it. Each unlock stays on their device for the
          duration you choose.
        </p>

        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {status?.enabled && !showForm && (
              <div className="p-4 rounded-lg bg-gray-50 space-y-4">
                <div className="flex items-start gap-3">
                  <div
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                    style={{ backgroundColor: `${brandColor}1A` }}
                  >
                    <KeyRound className="h-4 w-4" style={{ color: brandColor }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">Password is on</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {status.active_unlocks === 1 ? "1 active unlock" : `${status.active_unlocks} active unlocks`}
                      {status.updated_at && ` • Updated ${formatDate(status.updated_at)}`}
                    </p>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">Each unlock lasts</label>
                  <Select
                    value={String(status.grant_days ?? ACCESS_PASSWORD_GRANT_DAYS[0])}
                    onValueChange={handleDurationChange}
                    disabled={setMutation.isPending}
                  >
                    <SelectTrigger className="w-full min-h-[44px] text-base sm:text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCESS_PASSWORD_GRANT_DAYS.map((days) => (
                        <SelectItem key={days} value={String(days)}>
                          {DURATION_LABELS[days]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">Applies to new unlocks. Existing unlocks keep their expiry.</p>
                </div>

                {confirmRemove ? (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-3">
                    <p className="text-sm text-red-800">
                      Remove the password? Everyone who unlocked this genie with it loses access right away.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleRemove}
                        disabled={removeMutation.isPending}
                        variant="destructive"
                        className="flex-1 min-h-[44px]"
                      >
                        {removeMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Remove"}
                      </Button>
                      <Button onClick={() => setConfirmRemove(false)} variant="outline" className="flex-1 min-h-[44px]">
                        Keep it
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button onClick={openForm} variant="outline" className="flex-1 min-h-[44px]">
                      Change Password
                    </Button>
                    <Button
                      onClick={() => setConfirmRemove(true)}
                      variant="outline"
                      className="flex-1 min-h-[44px] text-red-600 hover:bg-red-50 hover:text-red-700"
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </div>
            )}

            {!isEnabled && !showForm && (
              <Button onClick={openForm} variant="outline" className="w-full min-h-[44px]">
                <KeyRound className="w-4 h-4 mr-2" />
                Add a Password
              </Button>
            )}

            {showForm && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label htmlFor="genie-access-password" className="text-sm font-medium text-gray-700">
                      {isEnabled ? "New password" : "Password"}
                    </label>
                    <button
                      type="button"
                      onClick={handleGenerate}
                      className="min-h-[44px] inline-flex items-center gap-1 text-sm font-medium"
                      style={{ color: brandColor }}
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      Conjure one
                    </button>
                  </div>
                  <div className="relative">
                    <Input
                      id="genie-access-password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={`At least ${ACCESS_PASSWORD_MIN_LENGTH} characters`}
                      className="pr-11 min-h-[44px] text-base sm:text-sm bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((visible) => !visible)}
                      className="absolute right-0 top-1/2 -translate-y-1/2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {isEnabled && (
                    <p className="text-xs text-amber-700">
                      Everyone who unlocked with the old password will need the new one.
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">Each unlock lasts</label>
                  <Select
                    value={String(grantDays)}
                    onValueChange={(value) => setGrantDays(Number(value) as AccessPasswordGrantDays)}
                  >
                    <SelectTrigger className="w-full min-h-[44px] text-base sm:text-sm bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCESS_PASSWORD_GRANT_DAYS.map((days) => (
                        <SelectItem key={days} value={String(days)}>
                          {DURATION_LABELS[days]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2 pt-1">
                  <Button
                    onClick={handleSave}
                    disabled={!isPasswordValid || setMutation.isPending}
                    className="flex-1 min-h-[44px] text-white"
                    style={{ backgroundColor: brandColor }}
                  >
                    {setMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save Password"
                    )}
                  </Button>
                  <Button onClick={() => setShowForm(false)} variant="outline" className="flex-1 min-h-[44px]">
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {savedPassword && (
              <div
                className="mt-4 p-4 rounded-lg space-y-3"
                style={{ backgroundColor: `${brandColor}10`, borderColor: brandColor, borderWidth: "1px" }}
              >
                <p className="text-sm font-medium" style={{ color: brandColor }}>
                  Your genie is sealed. Share the link and password with the people you want to let in. The
                  password won't be shown again.
                </p>
                <div
                  className="bg-white p-3 rounded font-mono text-xs whitespace-pre-wrap break-all text-gray-600"
                  style={{ borderColor: brandColor, borderWidth: "1px" }}
                >
                  {invitation}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => copyText(invitation, "Invitation copied")}
                    variant="ghost"
                    className="min-h-[44px]"
                    style={{ color: brandColor }}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Invitation
                  </Button>
                  <Button
                    onClick={() => copyText(savedPassword, "Password copied")}
                    variant="ghost"
                    className="min-h-[44px]"
                    style={{ color: brandColor }}
                  >
                    Copy Password
                  </Button>
                  <button
                    onClick={() => setSavedPassword(null)}
                    className="min-h-[44px] text-sm px-3"
                    style={{ color: brandColor }}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </ShareSettingsSection>
  );
}
